import type { Cents } from "./money";

/**
 * FINANCING & CAPITAL PLANNING entities (spec §73).
 *
 * These records hold what a person decided or a contract says — terms,
 * draws, payment-timing choices, rental assumptions, capital sources and
 * scenario fixtures. No figure is stored: balances, interest, cash
 * position and peak-cash all derive in `src/engine/financing.ts` and
 * `src/engine/capitalPlan.ts`.
 *
 * An UNKNOWN term is null and stays null until somebody confirms it.
 * Ambiguity is never converted into zero.
 */

export type Strategy = "flip" | "rental";

/**
 * Loan kinds are LABELS for the person, not different calculators: every
 * kind except 'refinance' runs the same ledger from the same terms
 * (spec §73.1C — the type never changes the math unless its terms do).
 */
export type LoanType =
  | "acquisition-rehab"
  | "acquisition"
  | "rehab"
  | "hard-money"
  | "private"
  | "business"
  | "permanent"
  | "refinance"
  | "other";
export type RateType = "fixed" | "variable";
export type InterestBasis = "drawn" | "committed" | "unknown";
export type PaymentCadence = "monthly" | "at-maturity" | "unknown";
export type RehabReimbursement = "reimbursed" | "advanced" | "unknown";
export type TermsSource = "manual" | "document" | "document-edited";
export type DayCountConvention = "act365" | "act360" | "30-360" | "monthly";
export type LoanStructure = "interest-only" | "amortizing" | "io-amortizing" | "revolving" | "balloon";
export type AmortizationStartRule = "after-io" | "includes-io";
export interface LoanRateChange { id: string; facilityId: string; date: string; ratePctX100: number; createdAt: string }
export interface LoanActivity {
  feeTarget?: "origination" | "draw" | "other";
  drawId?: string | null;
  dueDate?: string | null;
  id: string; facilityId: string; date: string;
  kind: "principal-payment" | "interest-payment" | "fee-payment" | "payoff" | "reversal";
  amountCents: Cents; principalCents: Cents; interestCents: Cents; feeCents: Cents;
  expenseId: string | null; reversalOfId: string | null; notes: string; createdAt: string;
}
/** Owner-only source history metadata. No balances or alternative financial authority. */
export interface WorkspaceFinancingHistory98 {
  activities: Array<LoanActivity & {projectId:string|null}>;
  draws: Array<LenderDraw & {projectId:string|null}>;
  limit:number;
  activityHasMore:boolean;
  drawHasMore:boolean;
}
export type LoanActivityInput = Pick<LoanActivity, "date" | "kind" | "amountCents"> & Partial<Pick<LoanActivity, "principalCents" | "interestCents" | "feeCents" | "expenseId" | "notes" | "feeTarget" | "dueDate" | "drawId">>;

export interface FinancingFacility {
  id: string;
  projectId: string | null;
  ownerScope?: "project" | "business";
  capitalSourceId?: string | null;
  dayCountConvention?: DayCountConvention | null;
  loanStructure?: LoanStructure | null;
  interestOnlyMonths?: number | null;
  amortizationMonths?: number | null;
  amortizationStartRule?: AmortizationStartRule | null;
  name: string;
  lender: string;
  loanType: LoanType;
  commitmentCents: Cents | null;
  acquisitionFundingCents: Cents | null;
  rehabFacilityCents: Cents | null;
  downPaymentCents: Cents | null;
  interestRatePctX100: number | null;
  rateType: RateType;
  interestBasis: InterestBasis;
  interestOnly: boolean;
  originationPointsX100: number | null;
  originationFeeCents: Cents | null;
  otherFeesCents: Cents | null;
  drawFeeCents: Cents | null;
  minimumDrawCents: Cents | null;
  paymentCadence: PaymentCadence;
  closingDate: string | null;
  firstPaymentDate: string | null;
  maturityDate: string | null;
  extensionNotes: string;
  prepaymentNotes: string;
  ltcPctX100: number | null;
  ltvPctX100: number | null;
  rehabReimbursement: RehabReimbursement;
  /** Lender's stated % of eligible rehab costs (informational; draws move the money). */
  rehabFundingPctX100: number | null;
  /** Refinance-only fields (loanType 'refinance'). */
  refinanceLtvPctX100: number | null;
  refinanceClosingCostsCents: Cents | null;
  /** The facility this refinance retires. Plain id — severed on delete. */
  payoffFacilityId: string | null;
  expectedDate: string | null;
  notes: string;
  termsSource: TermsSource;
  confirmedAt: string | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export type FacilityInputFields = Omit<
  FinancingFacility,
  "id" | "projectId" | "confirmedAt" | "archivedAt" | "createdAt" | "updatedAt"
>;

export type DrawStatus = "planned" | "ready" | "requested" | "approved" | "funded" | "rejected";

export interface LenderDraw {
  id: string;
  facilityId: string;
  label: string;
  status: DrawStatus;
  plannedDate: string | null;
  fundedDate: string | null;
  requestedCents: Cents | null;
  approvedCents: Cents | null;
  receivedCents: Cents | null;
  feeCents: Cents | null;
  /** Optional link to the schedule task the draw waits on. Plain id. */
  taskId: string | null;
  notes: string;
  sortOrder: number;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export type PaymentAnchor = "task-start" | "task-finish" | "date" | "project-start" | "project-finish";
export type PaymentSourceKind = "feature" | "quote" | "manual" | "facility" | "category";

/**
 * One tranche a person defined: "50% of the cabinet quote at task start."
 * Where no row exists the engine derives a LABELLED default assumption;
 * these rows are decisions and always win.
 */
export interface PaymentPlan {
  id: string;
  projectId: string;
  label: string;
  direction: "in" | "out";
  sourceKind: PaymentSourceKind;
  sourceId: string | null;
  pctX100: number | null;
  amountCents: Cents | null;
  anchor: PaymentAnchor;
  taskId: string | null;
  date: string | null;
  offsetDays: number;
  notes: string;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RentalAssumptions {
  projectId: string;
  monthlyRentCents: Cents | null;
  vacancyPctX100: number | null;
  propertyTaxAnnualCents: Cents | null;
  insuranceAnnualCents: Cents | null;
  hoaMonthlyCents: Cents | null;
  managementPctX100: number | null;
  maintenancePctX100: number | null;
  utilitiesMonthlyCents: Cents | null;
  otherMonthlyCents: Cents | null;
  leaseUpDays: number | null;
  updatedAt: string;
}

export interface VarianceNote {
  id: string;
  projectId: string;
  subjectKind: string;
  subjectId: string;
  note: string;
  createdAt: string;
}

/* ------------------------------------------------------------------ *
 * Business-level planning (workspace-wide, owned by no project)
 * ------------------------------------------------------------------ */

export type CapitalKind = "cash" | "owner" | "loc";

export interface CapitalSource {
  id: string;
  name: string;
  kind: CapitalKind;
  amountCents: Cents | null;
  locLimitCents: Cents | null;
  locBalanceCents: Cents | null;
  aprPctX100: number | null;
  notes: string;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * A scenario project is NOT a project: no job number, no accounting, no
 * statuses. Promoting one runs the ordinary project creation — never a
 * conversion (spec §73P).
 */
export interface ScenarioProject {
  id: string;
  name: string;
  strategy: Strategy;
  sourceProjectId: string | null;
  purchasePriceCents: Cents | null;
  rehabCents: Cents | null;
  durationDays: number | null;
  downPaymentCents: Cents | null;
  financingCommitmentCents: Cents | null;
  interestRatePctX100: number | null;
  originationFeeCents: Cents | null;
  expectedSaleCents: Cents | null;
  sellingCostsPctX100: number | null;
  monthlyRentCents: Cents | null;
  holdDaysAfterRehab: number | null;
  plannedStartDate: string | null;
  notes: string;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export type PlanObjective = "balanced" | "max-profit" | "max-throughput" | "min-peak-cash";

export interface PlanSettings {
  minReserveCents: Cents;
  horizonMonths: number;
  objective: PlanObjective;
  conservativeOverrunPctX100: number;
  conservativeDurationPctX100: number;
  conservativeDrawDelayDays: number;
  conservativeSaleDelayDays: number;
}

export const DEFAULT_PLAN_SETTINGS: PlanSettings = {
  minReserveCents: 0,
  horizonMonths: 12,
  objective: "balanced",
  conservativeOverrunPctX100: 1500,
  conservativeDurationPctX100: 2000,
  conservativeDrawDelayDays: 7,
  conservativeSaleDelayDays: 30,
};

/**
 * A PLANNING start override never mutates the project's committed schedule
 * (spec §73R): the CPM keeps its own start date; the portfolio shifts a
 * COPY of the derived events.
 */
export interface PlanOverride {
  subjectKind: "project" | "scenario";
  subjectId: string;
  startDate: string | null;
  updatedAt: string;
}
